'use client'

import type { ListingSearchContextValue } from '@/components/listings/search/ListingSearchProvider'
import { createPercentageValueAppliedComponent } from '@/components/listings/search/SearchFilters/shared/filterUtils'
import PercentageRangeFilter from '@/components/listings/search/SearchFilters/shared/NumericRangeFilter/PercentageRangeFilter'
import type {
  FilterProps,
  ListingFilter,
} from '@/components/listings/search/SearchFilters/types'

/**
 * Creates a Problem-Free Order Rate filter with the given A/B/C variant label.
 * Each call returns a new ListingFilter instance so that different variants
 * do not mutate a shared filter object.
 */
export function buildProblemFreeOrderRateFilter(
  variant: 'a' | 'b' | 'c'
): ListingFilter<number | null> {
  const label = `Filters.problemFreeOrderRate.${variant}`

  const filterFn = ({
    onChange,
    desktopScreen,
    value,
    onError,
  }: FilterProps<number | null>) => {
    const handleChange = (newValue: number) => {
      onChange(newValue === 0 ? null : newValue)
    }

    return (
      <PercentageRangeFilter
        selected={value}
        onChange={handleChange}
        onError={onError}
        desktopScreen={desktopScreen}
        analyticsTitle="Problem-Free Order Rate"
        defaultPercentage={0}
        inputLabel="Filters.problemFreeOrderRate.inputLabel"
        sliderStep={1}
        reverseTrack
      />
    )
  }

  filterFn.expandedOnInit = true
  filterFn.isAlwaysDisplayed = true
  filterFn.label = label
  filterFn.tooltip = 'Filters.problemFreeOrderRate.tooltip'
  filterFn.getOwnProps = (ctx: ListingSearchContextValue) => ({
    filterKey: 'problemFreeOrderRate' as const,
    onChange: (problemFreeOrderRate: number | null) => {
      ctx.updateFilters({ problemFreeOrderRate })
    },
    value: ctx.state.filters.problemFreeOrderRate,
    forLoggedUsersOnly: true,
  })
  filterFn.Applied = createPercentageValueAppliedComponent(
    'problemFreeOrderRate',
    label
  )

  return filterFn
}
